import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import RoleShell from '../../components/Enrollment/RoleShell';
import { Prod01 } from '../../constants/ActionType';
import DateRangePicker, { DateRangeSummary } from '../../components/common/DateRangePicker';
import { rangeFromSearch } from '../../utils/dateRange';
import { useSortableTable, SortableTh, nameFromString } from '../../components/SortableTable';
import { formatDate, formatTime } from '../../utils/dateFormat';

// Nurse Activity — <name>: drill-down from the dashboard nurse cards (Super Admin all orgs; Org Manager /
// Accounting own orgs; a nurse sees only her own page). The server enforces role + tenancy; this page only renders
// what it returns. Bonus = owner rules 2026-10-16 (rule version shown); open month = 'Projected (month in progress)'.
const PROGRAMS = ['RPM', 'CCM', 'PCM', 'APCM'];
const PROGRAM_COLOR = { RPM: '#1e88e5', CCM: '#43a047', PCM: '#fb8c00', APCM: '#8e24aa' };
const day = (d) => formatDate(d, '—');
const patientLink = (id) => ({ pathname: '/patientProfile', state: { patientid: id } });

const COLUMNS = [
    ['patientName', 'Patient'],
    ['program', 'Program'],
    ['minutesInRange', 'Minutes in range'],
    ['minutesThisMonth', 'Minutes this month'],
    ['needs', 'Billing progress'],
    ['lastContact', 'Last contact'],
    ['notes', 'Notes'],
    ['share', 'Share %'],
    ['bonusCredits', 'Bonus credits'],
    ['bonusTotal', 'Bonus $'],
];
const money = (n, d = 2) => `$${(Number(n) || 0).toFixed(d)}`;
const NURSE_ROLES = ['HrMedical', 'HrLead'];
const DOWNLOADS = {
    csv: { path: 'export', type: 'text/csv', label: 'Export CSV' },
    xlsx: { path: 'export/xlsx', type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', label: 'Download Excel' },
    pdf: { path: 'export/pdf', type: 'application/pdf', label: 'Download PDF' },
};

const PATIENT_SORT_COLUMNS = {
    patientName: { type: 'name', value: (r) => nameFromString(r?.patientName) },
    program: { type: 'string', value: (r) => r?.program },
    minutesInRange: { type: 'number', value: (r) => r?.minutesInRange },
    minutesThisMonth: { type: 'number', value: (r) => r?.minutesThisMonth },
    needs: { type: 'number', value: (r) => r?.progress?.needs },
    lastContact: { type: 'date', value: (r) => r?.lastContact },
    notes: { type: 'number', value: (r) => r?.notes },
    share: { type: 'number', value: (r) => r?.bonus?.sharePct },
    bonusCredits: { type: 'number', value: (r) => r?.bonus?.credits },
    bonusTotal: { type: 'number', value: (r) => r?.bonus?.total },
};

export const EarnedBonusCard = ({ bonus }) => {
    if (!bonus) return null;
    const t = bonus.totals;
    const projected = t.status === 'projected';
    return (
        <div className="p-3 mt-2" style={{ border: `1px solid ${projected ? '#f9a825' : '#2e7d32'}`, borderRadius: 8 }} data-testid="earned-bonus-card">
            <div className="d-flex justify-content-between flex-wrap" style={{ gap: 8 }}>
                <div>
                    <small className="text-muted">Earned bonus</small>
                    <div style={{ fontSize: 26, fontWeight: 700 }}>{money(t.total, 2)}</div>
                    <span className="badge" style={{ background: projected ? '#fff3cd' : '#e8f5e9', color: '#333' }}>{t.label}</span>
                </div>
                <div style={{ fontSize: 13 }}>
                    <div>Billable credits: <b>{t.credits}</b> ({money(t.billableDollars)})</div>
                    <div>Effort (non-billable) minutes: <b>{t.nonBillableMinutes}</b> ({money(t.effortDollars, 4)})</div>
                    <div>Excluded voided / duplicate-flagged: <b>{t.excluded.entries}</b> entries ({t.excluded.minutes} min)</div>
                    <div>Rule version: <b>{t.ruleVersions.join(', ')}</b></div>
                </div>
                <div style={{ fontSize: 13 }}>
                    {t.codes.map((c) => <div key={c.code}>{c.code}: {c.shareUnits} unit(s) × {c.weight} = {c.credits} cr ({money(c.dollars)})</div>)}
                    {!t.codes.length && <div className="text-muted">No billable codes yet</div>}
                </div>
            </div>
            {bonus.months.length > 1 && (
                <small className="d-block mt-1">
                    {bonus.months.map((m) => `${moment(m.key, 'YYYY-MM').format('MMM YYYY')}: ${money(m.total, 2)} (${m.label})`).join(' · ')}
                </small>
            )}
            {bonus.months.some((m) => m.flags.length) && <small className="d-block text-warning">{bonus.months.flatMap((m) => m.flags).join(' ')}</small>}
            <small className="d-block text-muted mt-1">{bonus.rule}</small>
        </div>
    );
};
const LOG_SORT_COLUMNS = {
    when: { type: 'date', value: (r) => (r?.date ? `${r.date} ${r.time || ''}`.trim() : null) },
    patient: { type: 'name', value: (r) => nameFromString(r?.patientName) },
    program: { type: 'string', value: (r) => r?.program },
    minutes: { type: 'number', value: (r) => r?.minutes },
    activityType: { type: 'string', value: (r) => r?.activityType },
};

export const sortPatients = (rows, key, dir) => {
    const val = (r) => (key === 'needs' ? (r.progress.needs === null ? Infinity : r.progress.needs) : r[key]);
    return [...rows].sort((a, b) => {
        const x = val(a);
        const y = val(b);
        const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x || '').localeCompare(String(y || ''));
        return dir === 'asc' ? c : -c;
    });
};

const Stat = ({ label, value }) => (
    <div className="col-6 col-md-3 mb-2">
        <div className="p-2 h-100" style={{ border: '1px solid #e5e9f0', borderRadius: 8 }}>
            <small className="text-muted">{label}</small>
            <div style={{ fontSize: 22, fontWeight: 600 }}>{value}</div>
        </div>
    </div>
);

const NurseActivity = () => {
    const { hrId } = useParams();
    const location = useLocation();
    const history = useHistory();
    const [range, setRange] = useState(() => rangeFromSearch(location.search));
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [belowOnly, setBelowOnly] = useState(false);
    const [exporting, setExporting] = useState(false);

    const load = useCallback(async (r) => {
        setLoading(true);
        setError('');
        try {
            const { data: d } = await axios.get(`${Prod01}/nurse-activity/${hrId}`, { params: r });
            setData(d);
        } catch (e) {
            setData(null);
            setError(e?.response?.status === 403 ? 'You do not have access to this nurse\'s activity.' : e?.response?.data?.message || 'Could not load nurse activity');
        } finally {
            setLoading(false);
        }
    }, [hrId]);

    useEffect(() => {
        history.replace({ pathname: location.pathname, search: `?startDate=${range.startDate}&endDate=${range.endDate}` });
        load(range);
    }, [range, load]); // eslint-disable-line react-hooks/exhaustive-deps

    const visiblePatients = useMemo(() => (data ? data.patients.filter((p) => !belowOnly || p.progress.belowThreshold) : []), [data, belowOnly]);
    const tableSort = useSortableTable(visiblePatients, { id: 'nurseActivity.patients', columns: PATIENT_SORT_COLUMNS, defaultSort: { key: 'patientName', dir: 'asc' } });
    const patients = tableSort.rows;
    const logSort = useSortableTable(data ? data.log : [], { id: 'nurseActivity.log', columns: LOG_SORT_COLUMNS, defaultSort: { key: 'when', dir: 'desc' } });

    const { user } = useSelector((state) => state.auth);
    const isNurse = NURSE_ROLES.includes(user?.role);
    const download = async (kind) => {
        const dl = DOWNLOADS[kind];
        setExporting(kind);
        try {
            const res = await axios.get(`${Prod01}/nurse-activity/${hrId}/${dl.path}`, { params: { ...range, ...(tableSort.sortKey ? { sortBy: tableSort.sortKey, sortDir: tableSort.sortDir } : {}), ...(logSort.sortKey ? { logSortBy: logSort.sortKey, logSortDir: logSort.sortDir } : {}) }, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: dl.type }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `nurse-activity-${range.startDate}-to-${range.endDate}.${kind}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            setError('Export failed');
        } finally {
            setExporting(false);
        }
    };

    const name = data ? `${data.nurse.firstname || ''} ${data.nurse.lastname || ''}`.trim() : '';
    const sortBtn = (key, label) => <SortableTh key={key} scope="col" sortKey={key} sort={tableSort}>{label}</SortableTh>;

    return (
        <RoleShell title={`Nurse Activity — ${name}`}>
            <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 12 }}>
                <h5 className="mb-0">Nurse Activity — {name || '…'}</h5>
                <div>
                    {!isNurse && <Link to="/adminDashboard" className="btn btn-sm btn-light mr-2">Back to dashboard</Link>}
                    {['pdf', 'xlsx', 'csv'].map((k) => (
                        <button key={k} type="button" className="edit-pill ml-2" onClick={() => download(k)} disabled={!data || !!exporting}>
                            {exporting === k ? 'Exporting…' : DOWNLOADS[k].label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="mt-3">
                <DateRangePicker id="nurse-activity-range" label="Date range" startDate={range.startDate} endDate={range.endDate} onChange={({ startDate, endDate }) => setRange({ startDate, endDate })} />
            </div>
            <DateRangeSummary startDate={range.startDate} endDate={range.endDate} />
            {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
            {loading && <small className="text-muted">Loading…</small>}
            {data && (
                <>
                    <div className="row mt-2">
                        {PROGRAMS.map((p) => <Stat key={p} label={`${p} minutes`} value={data.totals.byProgram[p] || 0} />)}
                        <Stat label="Total minutes" value={data.totals.minutes} />
                        <Stat label="Patients touched" value={data.totals.patientsTouched} />
                        <Stat label="Notes" value={data.totals.notes} />
                        <Stat label="Calls / interactions" value={data.totals.interactions} />
                        <Stat label="Avg minutes per patient" value={data.totals.avgMinutesPerPatient} />
                    </div>
                    <EarnedBonusCard bonus={data.bonus} />

                    <h6 className="mt-3">Daily minutes</h6>
                    <div style={{ width: '100%', height: 260 }} aria-label="Daily minutes chart">
                        <ResponsiveContainer>
                            <BarChart data={data.daily.map((d) => ({ ...d, label: moment(d.date, 'YYYY-MM-DD').format('MM/DD') }))}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="label" fontSize={11} />
                                <YAxis allowDecimals={false} fontSize={11} />
                                <Tooltip />
                                <Legend />
                                {PROGRAMS.map((p) => <Bar key={p} dataKey={p} stackId="m" fill={PROGRAM_COLOR[p]} />)}
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h6 className="mb-0">Patients ({patients.length})</h6>
                        <label className="mb-0">
                            <input type="checkbox" checked={belowOnly} onChange={(e) => setBelowOnly(e.target.checked)} /> Below billing threshold only
                        </label>
                    </div>
                    <small className="text-muted">Minutes this month = all staff, {moment(data.calendarMonth, 'YYYY-MM').format('MMMM YYYY')} (ET).</small>
                    <div className="table-responsive">
                        <table className="table table-sm table-striped mt-1" data-testid="patients-table">
                            <thead><tr>{COLUMNS.map(([k, l]) => sortBtn(k, l))}</tr></thead>
                            <tbody>
                                {patients.map((p) => (
                                    <tr key={`${p.patientId}-${p.program}`}>
                                        <td><Link to={patientLink(p.patientId)}>{p.patientName}</Link></td>
                                        <td>{p.program}</td>
                                        <td>{p.minutesInRange}</td>
                                        <td>{p.minutesThisMonth}</td>
                                        <td style={{ color: p.progress.belowThreshold ? '#b71c1c' : undefined }}>{p.progress.text}</td>
                                        <td>{day(p.lastContact)}</td>
                                        <td>{p.notes}</td>
                                        <td>{p.bonus ? `${p.bonus.sharePct}%` : '—'}</td>
                                        <td title={p.bonus?.codes || ''}>{p.bonus ? p.bonus.credits : '—'}</td>
                                        <td>{p.bonus ? money(p.bonus.total, 2) : '—'}</td>
                                    </tr>
                                ))}
                                {!patients.length && <tr><td colSpan={10} className="text-muted">No patients.</td></tr>}
                            </tbody>
                        </table>
                    </div>

                    <h6 className="mt-3">Assigned patients with no activity in this range ({data.noActivity.length})</h6>
                    {data.noActivity.length ? (
                        <ul className="mb-2" style={{ columns: 3 }}>
                            {data.noActivity.map((p) => <li key={p.patientId}><Link to={patientLink(p.patientId)}>{p.patientName}</Link></li>)}
                        </ul>
                    ) : <small className="text-muted">Every assigned patient has activity in this range.</small>}

                    <h6 className="mt-3">Time-entry log ({data.log.length})</h6>
                    <div className="table-responsive">
                        <table className="table table-sm table-bordered" data-testid="log-table">
                            <thead><tr><SortableTh sortKey="when" sort={logSort}>Date</SortableTh><SortableTh sortKey="patient" sort={logSort}>Patient</SortableTh><SortableTh sortKey="program" sort={logSort}>Program</SortableTh><SortableTh sortKey="minutes" sort={logSort}>Minutes</SortableTh><SortableTh sortKey="activityType" sort={logSort}>Activity type</SortableTh><th>Note</th></tr></thead>
                            <tbody>
                                {logSort.rows.map((r) => (
                                    <tr key={r.id}>
                                        <td>{day(r.date)} {formatTime(r.time)}</td>
                                        <td>{r.patientName}</td>
                                        <td>{r.program}</td>
                                        <td>{r.minutes}</td>
                                        <td>{r.activityType}</td>
                                        <td>{r.note ? <Link to={patientLink(r.patientId)} title={r.note}>{r.note.length > 40 ? `${r.note.slice(0, 40)}…` : r.note}</Link> : '—'}</td>
                                    </tr>
                                ))}
                                {!data.log.length && <tr><td colSpan={6} className="text-muted">No time entries in this range.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    <small className="text-muted">{data.definitions.interactions}</small>
                </>
            )}
        </RoleShell>
    );
};

export default NurseActivity;
